import { LyfeTooltip, Typography, useLazyDataQuery } from "@lyfegen-platform/react-common";
import { isEmptyValue } from "@lyfegen-platform/utils";
import { Box, IconButton } from "@material-ui/core";
import CallMadeIcon from "@material-ui/icons/CallMade";
import MailOpenIcon from "@material-ui/icons/DraftsOutlined";
import MailClosedIcon from "@material-ui/icons/MailOutlineOutlined";
import FlexTypographyComponent from "apps/platform-client/client/src/components/FlexTypographyComponent";
import { useDataMutation } from "apps/platform-client/client/src/hooks/useDataModule";
import { notificationInfoState } from "apps/platform-client/client/src/store/layout";
import { userPersonalInfoState } from "apps/platform-client/client/src/store/user";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { useSetRecoilState } from "recoil";
import { DocumentType, TaskStatus } from "../../../../models/generated/graphql";
import startUpper from "../../../../utils/start-upper";
import formatDateString from "../../../home/format-date-string";
import tasksStyles from "./inbox-tasks-styles";
import { READ_OR_UNREAD_TASK } from "./queries";
import { GET_USER_NOTIFICATION_COUNT } from "../../../user/queries";
import { NotificationsType } from "apps/platform-client/client/src/models/common/notifications";

interface InboxTaskContentProps {
	taskId: string;
	triggerEventId: number;
	content: InboxTaskContentPayload;
	taskDate: number | string;
	isVisible: boolean;
	taskStatus: TaskStatus | null;
	isCompleted: boolean;
	isAssignee: boolean;
}

export type InboxTaskContentPayload =
	| {
			triggerEventId: NotificationsType.AgreementApproval;
			agreementId: string;
			taskStatus: TaskStatus | null;
			agreementName: string;
			invoiceId: null;
			formType: null;
			caseId: null;
			orgCreator: null;
			type: null;
	  }
	| {
			triggerEventId: NotificationsType.RefundInvoiceReview;
			agreementId: null;
			taskStatus: TaskStatus | null;
			agreementName: null;
			invoiceId: string;
			formType: null;
			caseId: null;
			orgCreator: string;
			type: DocumentType;
	  }
	| {
			triggerEventId: NotificationsType.FormDueExact;
			agreementId: null;
			taskStatus: TaskStatus | null;
			agreementName: null;
			invoiceId: null;
			formType: string;
			caseId: string;
			orgCreator: null;
			type: null;
	  }
	| {
			triggerEventId: NotificationsType.NegotiableAttributeProposal;
			agreementId: null;
			taskStatus: TaskStatus | null;
			agreementName: null;
			invoiceId: null;
			formType: null;
			caseId: string;
			orgCreator: null;
			type: null;
	  };

const getDocumentType = (docType: DocumentType | null) => {
	if (docType === DocumentType.CreditNote) return "sent-refund-credit-note";
	if (docType === DocumentType.FreeOfCharge) return "sent-free-of-charge";

	return "refund-invoice";
};

export function formatTaskContent(triggerEventId: number, content: InboxTaskContentPayload, t: (key: string, options?: any) => any) {
	switch (triggerEventId) {
		case NotificationsType.AgreementApproval:
			return (
				<FlexTypographyComponent variant="inherit" text={`${t("new-agreement")} ${content.agreementName} ${t("needs-approval")}`} />
			);

		case NotificationsType.FormDueExact:
			return (
				<FlexTypographyComponent
					variant="inherit"
					text={`${t(content.formType || "")} ${t("for-case")} ${content.caseId} ${t("is-due")}`}
				/>
			);
		case NotificationsType.RefundInvoiceReview:
			return (
				<FlexTypographyComponent
					variant="inherit"
					text={`${content.orgCreator} ${t(getDocumentType(content.type))} ${content.invoiceId} ${t(
						"for-your-organization-review"
					)}`}
				/>
			);
		case NotificationsType.RefundInvoiceResendForReview:
			return (
				<FlexTypographyComponent
					variant="inherit"
					text={`${t("task-document-resend-for-review", {
						organizationName: content.orgCreator,
						documentType: t(getDocumentType(content.type)),
						documentID: content.invoiceId,
					})}`}
				/>
			);
		case NotificationsType.RefundInvoiceSenderRejection:
		case NotificationsType.RefundInvoiceReceiverRejection:
			return (
				<FlexTypographyComponent
					variant="inherit"
					text={`${startUpper(t(getDocumentType(content.type)))} ${content.invoiceId} ${t("receiver-rejection")}`}
				/>
			);
		case NotificationsType.RefundInvoiceSenderConfirmation:
			return (
				<FlexTypographyComponent
					variant="inherit"
					text={`${startUpper(t(getDocumentType(content.type)))} ${content.invoiceId} ${t("sender-post-receiver-confirmation")}`}
				/>
			);
		case NotificationsType.RefundInvoiceReceiverConfirmation:
			return (
				<FlexTypographyComponent
					variant="inherit"
					text={`${startUpper(t(getDocumentType(content.type)))} ${content.invoiceId} ${t("receiver-approval")}`}
				/>
			);

		case NotificationsType.RefundInvoiceReceiverApproval:
			return (
				<FlexTypographyComponent
					variant="inherit"
					text={`${startUpper(t(getDocumentType(content.type)))} ${content.invoiceId} ${t("sender-confirmation", {
						organizationName: content.orgCreator,
					})}`}
				/>
			);
		case NotificationsType.NegotiableAttributeProposal:
			return (
				<FlexTypographyComponent
					variant="inherit"
					text={`${t("new-case-specific-attribute-proposal")} ${content.caseId} ${t("needs-your-review")}.`}
				/>
			);
		default:
			return <></>;
	}
}

const InboxTaskContent: React.FC<InboxTaskContentProps> = ({
	triggerEventId,
	content,
	taskDate,
	isVisible,
	isCompleted,
	taskStatus,
	taskId,
	isAssignee,
}) => {
	const { goToTaskContainer, goToTaskContainerWrap, task, isReadDot, iconButton } = tasksStyles();
	const [temTaskStatus, setTaskStatus] = useState(taskStatus && taskStatus === TaskStatus.Unread);
	const setOpenNotificationPopup = useSetRecoilState(notificationInfoState);
	const { t } = useTranslation();
	const { push } = useHistory();
	const [mutate] = useDataMutation(READ_OR_UNREAD_TASK);
	const [loadNotificationsCount] = useLazyDataQuery(GET_USER_NOTIFICATION_COUNT, { fetchPolicy: "no-cache" });
	const setUserData = useSetRecoilState(userPersonalInfoState);

	function goToTaskHandle(triggerEventId: number) {
		if (temTaskStatus) {
			markTaskAsReadOrUnread();
		}
		setOpenNotificationPopup(false);

		switch (triggerEventId) {
			case NotificationsType.AgreementApproval:
				push(`/agreements/agreement?id=${content?.agreementId}&tab=0`);
				return;
			case NotificationsType.NegotiableAttributeProposal:
			case NotificationsType.FormDueExact:
				push(`/cases/case?id=${content.caseId}`);
				return;
			case NotificationsType.RefundInvoiceReview:
			case NotificationsType.RefundInvoiceResendForReview:
			case NotificationsType.RefundInvoiceReceiverApproval:
			case NotificationsType.RefundInvoiceReceiverRejection:
			case NotificationsType.RefundInvoiceReceiverConfirmation:
			case NotificationsType.RefundInvoiceSenderConfirmation:
			case NotificationsType.RefundInvoiceSenderRejection:
				push(`/financials/documents/selector/${content.invoiceId}`);
				return;
		}
	}

	function markTaskAsReadOrUnread(goToEvent?: boolean) {
		const currentTaskStatus = !temTaskStatus && (isEmptyValue(goToEvent) || !goToEvent);
		mutate({
			variables: {
				taskId: taskId,
				taskStatus: currentTaskStatus ? TaskStatus.Unread : TaskStatus.Read,
			},
			onCompleted: async () => {
				setTaskStatus((prev) => !prev);
				const updatedNotificationCount = await loadNotificationsCount();
				if (isAssignee) {
					setUserData((prev) => {
						return { ...prev, notificationCount: updatedNotificationCount.data.GetNotificationCount.notificationCount };
					});
				}
			},
		});
	}

	return (
		<Box display="inline">
			{[
				NotificationsType.AgreementApproval,
				NotificationsType.FormDueExact,
				NotificationsType.RefundInvoiceReview,
				NotificationsType.RefundInvoiceResendForReview,
				NotificationsType.NegotiableAttributeProposal,
				NotificationsType.RefundInvoiceReceiverApproval,
				NotificationsType.RefundInvoiceReceiverRejection,
				NotificationsType.RefundInvoiceReceiverConfirmation,
				NotificationsType.RefundInvoiceSenderConfirmation,
				NotificationsType.RefundInvoiceSenderRejection,
			].includes(triggerEventId) && (
				<Box
					display="flex"
					alignItems={"center"}
					justifyContent="space-between"
					flexWrap={"nowrap"}
					className={task}
					style={{
						fontWeight: temTaskStatus && !isCompleted ? 600 : 400,
					}}
				>
					<Box display="flex" alignItems={"center"} style={{ width: "50%" }}>
						<span
							style={{
								opacity: temTaskStatus && isAssignee && !isCompleted ? 1 : 0,
							}}
							className={isReadDot}
						/>

						<Box style={{ maxWidth: "100%" }}>{formatTaskContent(triggerEventId, content, t)}</Box>
					</Box>
					<Box className={goToTaskContainerWrap} style={{ visibility: isVisible ? "visible" : "hidden" }}>
						<Box
							onClick={() => goToTaskHandle(triggerEventId)}
							className={goToTaskContainer}
							style={{ marginRight: isCompleted ? 40 : 0 }}
						>
							<Typography type="bold" style={{ marginLeft: "12px" }}>
								{t("go-to-task")}
							</Typography>{" "}
							<IconButton>
								<CallMadeIcon style={{ width: 15 }} color="primary" />
							</IconButton>
						</Box>
						{!isCompleted && (
							<IconButton className={iconButton} onClick={() => markTaskAsReadOrUnread()}>
								{temTaskStatus && !isCompleted ? (
									<LyfeTooltip placement="right-end" title={t("mark-as-read")}>
										<MailOpenIcon color="primary" />
									</LyfeTooltip>
								) : (
									<LyfeTooltip placement="right-end" title={t("mark-as-unread")}>
										<MailClosedIcon color="primary" />
									</LyfeTooltip>
								)}
							</IconButton>
						)}
					</Box>
					<Box style={{ flexBasis: "18%", textAlign: "right" }}>
						<Typography type="inherit" variant="caption">
							{formatDateString(taskDate, t, true)}
						</Typography>{" "}
					</Box>
				</Box>
			)}
		</Box>
	);
};

export default InboxTaskContent;
